import React, { useState, useEffect, useCallback, useRef } from 'react';

const smartCapitalize = (str) => str ? str.replace(/\b[a-z]/g, c => c.toUpperCase()) : str;
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from '../../components/ui/alert-dialog';
import { Label } from '../../components/ui/label';
import { Spinner } from '../../components/ui/spinner';
import { useToast } from '../../hooks/use-toast';
import { formatCurrency } from '../../lib/i18n';
import { Switch } from '../../components/ui/switch';
import {
  Plus,
  Search,
  Package,
  AlertTriangle,
  Edit,
  Trash2,
  TrendingUp,
  TrendingDown,
  Settings,
  Eye,
  EyeOff,
  X,
  Minus,
  Sparkles
} from 'lucide-react';
import AIImportView from './AIImportView';
import { PremiumUpgradeDialog } from '../../components/ui/premium-overlay';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_CATEGORIES = [
  { value: 'engine', label: 'Engine', labelBn: 'ইঞ্জিন', isDefault: true },
  { value: 'brake', label: 'Brake', labelBn: 'ব্রেক', isDefault: true },
  { value: 'electrical', label: 'Electrical', labelBn: 'ইলেকট্রিক্যাল', isDefault: true },
  { value: 'transmission', label: 'Transmission', labelBn: 'ট্রান্সমিশন', isDefault: true },
  { value: 'accessories', label: 'Accessories', labelBn: 'আনুষঙ্গিক', isDefault: true },
  { value: 'lubricants', label: 'Lubricants', labelBn: 'লুব্রিকেন্টস', isDefault: true },
  { value: 'filters', label: 'Filters', labelBn: 'ফিল্টার', isDefault: true },
  { value: 'bearings', label: 'Bearings', labelBn: 'বিয়ারিং', isDefault: true },
  { value: 'suspension', label: 'Suspension', labelBn: 'সাসপেনশন', isDefault: true },
  { value: 'clutch', label: 'Clutch', labelBn: 'ক্লাচ', isDefault: true },
  { value: 'tires', label: 'Tires & Tubes', labelBn: 'টায়ার ও টিউব', isDefault: true },
  { value: 'cables', label: 'Cables', labelBn: 'ক্যাবল', isDefault: true },
  { value: 'body_parts', label: 'Body Parts', labelBn: 'বডি পার্টস', isDefault: true },
];

// Required fields for add/edit part form
const REQUIRED_FIELDS = ['name', 'category', 'purchase_price', 'selling_price', 'stock_quantity'];

// Keyword-to-category mapping for fast local matching (primary fallback, no API cost)
const CATEGORY_KEYWORDS = {
  engine: ['piston', 'ring', 'cylinder', 'crankshaft', 'camshaft', 'valve', 'gasket', 'head', 'block', 'engine', 'carburetor', 'injector', 'throttle', 'rocker', 'timing', 'chain', 'sprocket'],
  brake: ['brake', 'pad', 'disc', 'drum', 'caliper', 'shoe', 'lever', 'master cylinder', 'rotor'],
  electrical: ['bulb', 'light', 'lamp', 'battery', 'fuse', 'relay', 'switch', 'wire', 'harness', 'cdi', 'coil', 'ignition', 'starter', 'regulator', 'rectifier', 'indicator', 'horn', 'headlight', 'tail light', 'meter', 'speedometer'],
  transmission: ['gear', 'gearbox', 'shaft', 'pinion', 'sprocket', 'transmission'],
  accessories: ['mirror', 'stand', 'seat', 'grip', 'handlebar', 'footrest', 'mudguard', 'fender', 'visor', 'lock', 'key'],
  lubricants: ['oil', 'lubricant', 'grease', 'coolant', 'fluid', 'mobil', 'motul', 'castrol'],
  filters: ['filter', 'air filter', 'oil filter', 'fuel filter'],
  bearings: ['bearing', 'ball bearing', 'roller bearing', 'bush', 'bushing'],
  suspension: ['shock', 'absorber', 'spring', 'fork', 'suspension', 'damper', 'linkage'],
  clutch: ['clutch', 'plate', 'pressure plate', 'clutch cable', 'clutch lever', 'friction'],
  tires: ['tire', 'tyre', 'tube', 'rim', 'wheel', 'spoke', 'nipple'],
  cables: ['cable', 'throttle cable', 'brake cable', 'clutch cable', 'speedometer cable', 'choke cable'],
  body_parts: ['body', 'panel', 'fairing', 'tank', 'cover', 'side panel', 'leg guard', 'chain cover', 'silencer', 'exhaust', 'muffler'],
};

export default function InventoryPage() {
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const { isPremium } = useAuth();
  const [parts, setParts] = useState([]);
  const [lowStockParts, setLowStockParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [showAddPart, setShowAddPart] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [adjustingPart, setAdjustingPart] = useState(null);
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'import'
  const [pendingReviewCount, setPendingReviewCount] = useState(0);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [adjustMode, setAdjustMode] = useState('set'); // 'set', 'add', 'remove'
  const [adjustmentValue, setAdjustmentValue] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [revealedPrices, setRevealedPrices] = useState({});
  const [saving, setSaving] = useState(false);
  const lastCategoryRef = useRef('engine');
  const adjustInputRef = useRef(null);

  // Pagination state
  const PAGE_SIZE = 50;
  const [totalCount, setTotalCount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  // Confirm dialog state (replaces window.confirm)
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', description: '', onConfirm: null });

  // Category management state
  const [showCategorySettings, setShowCategorySettings] = useState(false);
  const [customCategories, setCustomCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryNameBn, setNewCategoryNameBn] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [disabledDefaults, setDisabledDefaults] = useState([]);
  const [consumableSuggest, setConsumableSuggest] = useState(null); // { category_id, category_name, confidence }
  const [consumableCategories, setConsumableCategories] = useState([]);
  const [consumableCategoryId, setConsumableCategoryId] = useState('');
  const [isConsumable, setIsConsumable] = useState(true);
  const userToggledConsumableRef = useRef(false); // Track if user manually toggled the non-consumable switch
  const [remindersEnabled, setRemindersEnabled] = useState(false);

  const emptyPart = {
    name: '', name_bn: '', sku: '', category: 'engine', brand: '',
    purchase_price: '', selling_price: '', stock_quantity: '', min_stock_level: '5'
  };
  const [formData, setFormData] = useState(emptyPart);

  // Build combined categories list (include active custom categories only in selectors)
  const allCategories = [
    ...DEFAULT_CATEGORIES.filter(c => !disabledDefaults.includes(c.value)),
    ...customCategories.filter(c => c.is_active !== false).map(c => ({ value: c.name.toLowerCase().replace(/\s+/g, '_'), label: c.name, labelBn: c.name_bn || c.name, id: c.id }))
  ];

  const filterCategories = [
    { value: 'all', label: 'All', labelBn: 'সব' },
    ...allCategories
  ];

  useEffect(() => {
    fetchParts(true);
    fetchLowStock();
    fetchCustomCategories();
    fetchCategorySettings();
    fetchConsumableCategories();
    fetchReminderSettings();
    // Check for pending import reviews
    axios.get(`${API}/import/batches/pending`).then(r => {
      setPendingReviewCount(r.data?.pending_items || 0);
    }).catch(() => {});
  }, [category, showLowStockOnly]);

  // Debounced auto-search on typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true);
      fetchParts(true);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  // Debounced consumable category auto-suggest while typing part name
  useEffect(() => {
    if (!formData.name || formData.name.length < 3 || !remindersEnabled) {
      setConsumableSuggest(null);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API}/marketing/consumable-categories/classify-single`, {
          part_name: formData.name,
          category: formData.category || '',
        });
        if (data.confidence === 'high' || data.confidence === 'medium') {
          // Check if the suggested category is "Not Consumable" — auto-flip toggle only if user hasn't manually toggled
          const suggestedCat = consumableCategories.find(c => c.id === data.suggested_category_id);
          if (!userToggledConsumableRef.current) {
            if (suggestedCat?.is_non_consumable || suggestedCat?.name?.toLowerCase() === 'not consumable') {
              setIsConsumable(false);
              setConsumableCategoryId(data.suggested_category_id);
              setConsumableSuggest(data);
            } else {
              // Consumable category — always flip back to consumable
              setIsConsumable(true);
              setConsumableSuggest(data);
              setConsumableCategoryId(data.suggested_category_id);
            }
          } else {
            // User manually toggled — only update suggestion display, don't flip the toggle
            setConsumableSuggest(data);
            if (suggestedCat && !suggestedCat.is_non_consumable) {
              setConsumableCategoryId(data.suggested_category_id);
            }
          }
        } else {
          setConsumableSuggest(null);
        }
      } catch { setConsumableSuggest(null); }
    }, 600);
    return () => clearTimeout(timer);
  }, [formData.name, formData.category, isConsumable, remindersEnabled, consumableCategories]);

  // Auto-suggest system category: keyword matching first, Gemini AI fallback (max 3 calls per part session)
  const userChangedCategoryRef = useRef(false);
  const geminiCallCountRef = useRef(0);
  useEffect(() => {
    if (editingPart || !formData.name || formData.name.length < 2 || userChangedCategoryRef.current) return;

    // Step 1: Try keyword matching locally (instant, no API cost)
    const nameLower = formData.name.toLowerCase();
    let bestMatch = null;
    let bestLen = 0;
    for (const [cat, keywords] of Object.entries(CATEGORY_KEYWORDS)) {
      for (const kw of keywords) {
        if (nameLower.includes(kw) && kw.length > bestLen) {
          const catExists = allCategories.some(c => c.value === cat);
          if (catExists) {
            bestMatch = cat;
            bestLen = kw.length;
          }
        }
      }
    }
    if (bestMatch && bestMatch !== formData.category) {
      setFormData(p => ({ ...p, category: bestMatch }));
      return; // Keyword match found — skip Gemini
    }

    // Step 2: If keyword match fails and Gemini budget remaining, call AI (longer debounce)
    if (formData.name.length < 4 || geminiCallCountRef.current >= 3) return;
    const timer = setTimeout(async () => {
      if (geminiCallCountRef.current >= 3) return;
      try {
        geminiCallCountRef.current += 1;
        const { data } = await axios.post(`${API}/parts/suggest-category`, {
          part_name: formData.name,
        });
        if ((data.confidence === 'high' || data.confidence === 'medium') && data.category) {
          const catExists = allCategories.some(c => c.value === data.category);
          if (catExists && data.category !== formData.category) {
            setFormData(p => ({ ...p, category: data.category }));
          }
        }
      } catch { /* ignore */ }
    }, 1500);
    return () => clearTimeout(timer);
  }, [formData.name, editingPart, allCategories]);

  // Auto-set min_stock_level to 20% (rounded up) of stock_quantity for new parts
  const userChangedMinStockRef = useRef(false);
  useEffect(() => {
    if (editingPart || userChangedMinStockRef.current) return;
    const qty = parseInt(formData.stock_quantity, 10);
    if (!qty || qty <= 0) return;
    const minStock = Math.ceil(qty * 0.2);
    setFormData(p => ({ ...p, min_stock_level: minStock.toString() }));
  }, [formData.stock_quantity, editingPart]);

  // Auto-focus quantity input when adjust stock dialog opens or mode changes
  useEffect(() => {
    if (showAdjustDialog) {
      setTimeout(() => adjustInputRef.current?.focus(), 100);
    }
  }, [showAdjustDialog, adjustMode]);

  const buildQueryParams = () => {
    const params = new URLSearchParams();
    if (category !== 'all') params.append('category', category);
    if (search) params.append('search', search);
    if (showLowStockOnly) params.append('low_stock', 'true');
    return params;
  };

  const fetchParts = async (reset = false) => {
    try {
      const params = buildQueryParams();
      const skip = reset ? 0 : parts.length;
      params.append('skip', skip);
      params.append('limit', PAGE_SIZE);
      
      const [partsRes, countRes] = await Promise.all([
        axios.get(`${API}/parts?${params}`),
        reset ? axios.get(`${API}/parts/count?${buildQueryParams()}`) : Promise.resolve(null)
      ]);

      if (reset) {
        setParts(partsRes.data);
        setTotalCount(countRes?.data?.count ?? partsRes.data.length);
      } else {
        setParts(prev => [...prev, ...partsRes.data]);
      }
    } catch (error) {
      console.error('Failed to fetch parts:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    setLoadingMore(true);
    fetchParts(false);
  };

  const fetchLowStock = async () => {
    try {
      const response = await axios.get(`${API}/parts/low-stock`);
      setLowStockParts(response.data);
    } catch (error) {
      console.error('Failed to fetch low stock:', error);
    }
  };

  const fetchCustomCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories/inventory`);
      setCustomCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchCategorySettings = async () => {
    try {
      const response = await axios.get(`${API}/category-settings/inventory`);
      setDisabledDefaults(response.data.disabled_defaults || []);
    } catch (error) {
      console.error('Failed to fetch category settings:', error);
    }
  };

  const fetchConsumableCategories = async () => {
    try {
      const { data } = await axios.get(`${API}/marketing/consumable-categories`);
      setConsumableCategories(data);
    } catch { /* ignore */ }
  };

  const fetchReminderSettings = async () => {
    try {
      const { data } = await axios.get(`${API}/marketing/reminders/settings`);
      setRemindersEnabled(data.is_enabled === true);
    } catch { /* ignore */ }
  };

  // Validate required fields
  const validateForm = () => {
    const errors = {};
    const requiredForMode = editingPart
      ? REQUIRED_FIELDS.filter(f => f !== 'stock_quantity')
      : REQUIRED_FIELDS;
    requiredForMode.forEach(field => {
      if (!formData[field] || formData[field].toString().trim() === '') {
        errors[field] = true;
      }
    });
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Integer-only handler for price/stock fields (no negative, no symbols)
  const handleIntegerField = (field, value) => {
    if (value === '') {
      setFormData(p => ({ ...p, [field]: value }));
      return;
    }
    // Strip any non-digit characters
    const cleaned = value.replace(/\D/g, '');
    if (cleaned === '') {
      setFormData(p => ({ ...p, [field]: '' }));
      return;
    }
    const intVal = parseInt(cleaned, 10);
    if (!isNaN(intVal)) {
      setFormData(p => ({ ...p, [field]: intVal.toString() }));
    }
  };

  const handleSavePart = async () => {
    if (!validateForm()) {
      toast({ title: language === 'en' ? 'Please fill all required fields' : 'সব আবশ্যক ক্ষেত্র পূরণ করুন', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        purchase_price: parseInt(formData.purchase_price, 10),
        selling_price: parseInt(formData.selling_price, 10),
        stock_quantity: parseInt(formData.stock_quantity, 10),
        min_stock_level: parseInt(formData.min_stock_level, 10) || 5
      };

      let savedPartId;
      if (editingPart) {
        // Don't send stock_quantity in edit mode - use adjust stock instead
        delete payload.stock_quantity;
        await axios.put(`${API}/parts/${editingPart.id}`, payload);
        savedPartId = editingPart.id;
        toast({ title: language === 'en' ? 'Part updated!' : 'পার্ট আপডেট হয়েছে!' });
      } else {
        lastCategoryRef.current = formData.category;
        const res = await axios.post(`${API}/parts`, payload);
        savedPartId = res.data?.id;
        toast({ title: language === 'en' ? 'Part added!' : 'পার্ট যোগ হয়েছে!' });
      }

      // Map to consumable category if selected
      if (savedPartId && consumableCategoryId) {
        try {
          await axios.post(`${API}/marketing/consumable-categories/map-part`, {
            part_id: savedPartId,
            consumable_category_id: consumableCategoryId,
          });
        } catch { /* best effort */ }
      } else if (savedPartId && !isConsumable && !consumableCategoryId) {
        // Fallback: user toggled "Not consumable" but category ID wasn't set
        const notConsumCat = consumableCategories.find(c => c.is_non_consumable || c.name?.toLowerCase() === 'not consumable');
        if (notConsumCat) {
          try {
            await axios.post(`${API}/marketing/consumable-categories/map-part`, {
              part_id: savedPartId,
              consumable_category_id: notConsumCat.id,
            });
          } catch { /* best effort */ }
        }
      }

      setShowAddPart(false);
      setEditingPart(null);
      setFormData(emptyPart);
      setFieldErrors({});
      setConsumableSuggest(null);
      setConsumableCategoryId('');
      setIsConsumable(true);
      fetchParts(true);
      fetchLowStock();
    } catch (error) {
      toast({ title: language === 'en' ? 'Failed to save part' : 'পার্ট সংরক্ষণ ব্যর্থ', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleEditPart = (part) => {
    setEditingPart(part);
    setFormData({
      name: part.name || '',
      name_bn: part.name_bn || '',
      sku: part.sku || '',
      category: part.category || 'engine',
      brand: part.brand || '',
      purchase_price: part.purchase_price?.toString() || '',
      selling_price: part.selling_price?.toString() || '',
      stock_quantity: part.stock_quantity?.toString() || '',
      min_stock_level: part.min_stock_level?.toString() || '5'
    });
    setFieldErrors({});
    // Load existing consumable mapping for this part
    setConsumableCategoryId('');
    setIsConsumable(true);
    setConsumableSuggest(null);
    if (remindersEnabled && part.id) {
      axios.get(`${API}/marketing/part-category-map`).then(({ data }) => {
        if (data[part.id]) {
          const match = consumableCategories.find(c => c.name === data[part.id]);
          if (match) setConsumableCategoryId(match.id);
        }
      }).catch(() => {});
    }
    setShowAddPart(true);
  };

  const handleDeletePart = (partId) => {
    setConfirmDialog({
      open: true,
      title: language === 'en' ? 'Delete Part' : 'পার্ট মুছুন',
      description: language === 'en' ? 'Are you sure you want to delete this part? This action cannot be undone.' : 'আপনি কি নিশ্চিত যে এই পার্টটি মুছে ফেলতে চান? এই ক্রিয়াটি পূর্বাবস্থায় ফেরানো যাবে না।',
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/parts/${partId}`);
          toast({ title: language === 'en' ? 'Part deleted!' : 'পার্ট মুছে ফেলা হয়েছে!' });
          fetchParts(true);
          fetchLowStock();
        } catch (error) {
          toast({ title: language === 'en' ? 'Failed to delete' : 'মুছতে ব্যর্থ', variant: 'destructive' });
        }
      }
    });
  };

  const openAdjustStock = (part) => {
    setAdjustingPart(part);
    setAdjustMode('set');
    setAdjustmentValue(part.stock_quantity.toString());
    setShowAdjustDialog(true);
  };

  const getNewStockQuantity = () => {
    const val = parseInt(adjustmentValue, 10) || 0;
    if (!adjustingPart) return 0;
    switch (adjustMode) {
      case 'set': return val;
      case 'add': return adjustingPart.stock_quantity + val;
      case 'remove': return Math.max(0, adjustingPart.stock_quantity - val);
      default: return adjustingPart.stock_quantity;
    }
  };

  const handleAdjustStock = async () => {
    if (!adjustingPart) return;
    const newQty = getNewStockQuantity();
    const adjustment = newQty - adjustingPart.stock_quantity;
    if (adjustment === 0) {
      setShowAdjustDialog(false);
      return;
    }
    try {
      await axios.post(`${API}/parts/${adjustingPart.id}/adjust-stock?adjustment=${adjustment}&reason=manual`);
      toast({ title: language === 'en' ? 'Stock adjusted!' : 'স্টক সমন্বয় হয়েছে!' });
      setShowAdjustDialog(false);
      setAdjustingPart(null);
      setAdjustmentValue('');
      fetchParts(true);
      fetchLowStock();
    } catch (error) {
      toast({ title: error.response?.data?.detail || 'Failed to adjust stock', variant: 'destructive' });
    }
  };

  // Category management handlers
  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      await axios.post(`${API}/categories`, { name: newCategoryName.trim(), name_bn: newCategoryNameBn.trim() || null, module: 'inventory' });
      toast({ title: language === 'en' ? 'Category added!' : 'ক্যাটাগরি যোগ হয়েছে!' });
      setNewCategoryName('');
      setNewCategoryNameBn('');
      fetchCustomCategories();
    } catch (error) {
      toast({ title: language === 'en' ? 'Failed to add category' : 'ক্যাটাগরি যোগ ব্যর্থ', variant: 'destructive' });
    }
  };

  const handleUpdateCategory = async (catId) => {
    if (!editingCategory) return;
    try {
      await axios.put(`${API}/categories/${catId}`, { name: editingCategory.name, name_bn: editingCategory.name_bn || null, module: 'inventory' });
      toast({ title: language === 'en' ? 'Category updated!' : 'ক্যাটাগরি আপডেট হয়েছে!' });
      setEditingCategory(null);
      fetchCustomCategories();
    } catch (error) {
      toast({ title: language === 'en' ? 'Failed to update' : 'আপডেট ব্যর্থ', variant: 'destructive' });
    }
  };

  const handleToggleCustomCategory = async (catId) => {
    try {
      await axios.patch(`${API}/categories/${catId}/toggle`);
      fetchCustomCategories();
    } catch (error) {
      toast({ title: language === 'en' ? 'Failed to toggle' : 'টগল ব্যর্থ', variant: 'destructive' });
    }
  };

  const handleDeleteCategory = (catId) => {
    setConfirmDialog({
      open: true,
      title: language === 'en' ? 'Delete Category' : 'ক্যাটাগরি মুছুন',
      description: language === 'en' ? 'Are you sure you want to delete this category?' : 'আপনি কি নিশ্চিত যে এই ক্যাটাগরিটি মুছে ফেলতে চান?',
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/categories/${catId}`);
          toast({ title: language === 'en' ? 'Category deleted!' : 'ক্যাটাগরি মুছে ফেলা হয়েছে!' });
          fetchCustomCategories();
        } catch (error) {
          toast({ title: language === 'en' ? 'Failed to delete' : 'মুছতে ব্যর্থ', variant: 'destructive' });
        }
      }
    });
  };

  const toggleDefaultCategory = async (value) => {
    const newDisabled = disabledDefaults.includes(value)
      ? disabledDefaults.filter(v => v !== value)
      : [...disabledDefaults, value];
    setDisabledDefaults(newDisabled);
    try {
      await axios.put(`${API}/category-settings/inventory`, { disabled_defaults: newDisabled });
    } catch (error) {
      console.error('Failed to save category settings:', error);
    }
  };

  // Error border class helper
  const errorBorder = (field) => fieldErrors[field] ? 'border-red-500 ring-1 ring-red-500' : '';

  // Required label helper
  const RequiredLabel = ({ children }) => (
    <Label className="flex items-center gap-1">
      {children}
      <span className="text-red-500">*</span>
    </Label>
  );

  // Toggle buy price reveal on tap/click
  const toggleRevealPrice = (partId) => {
    setRevealedPrices(p => ({ ...p, [partId]: !p[partId] }));
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header — hide when in import review mode */}
        {viewMode !== 'import' && (
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold font-['Barlow_Condensed']">{t('inventory_title')}</h1>
            <p className="text-slate-500">{language === 'en' ? `${totalCount} parts in inventory` : `ইনভেন্টরিতে ${totalCount}টি পার্ট`}</p>
          </div>
          <div className="flex gap-2">
            {/* Category Settings Button */}
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => setShowCategorySettings(true)}
              data-testid="category-settings-btn"
            >
              <Settings className="w-4 h-4" />
              {language === 'en' ? 'Categories' : 'ক্যাটাগরি'}
            </Button>
            {/* Smart Import Button */}
              <Button
                variant="outline"
                className="gap-2 border-blue-600 text-blue-700 hover:bg-blue-50"
                onClick={() => isPremium ? setViewMode('import') : setShowUpgradeDialog(true)}
                data-testid="smart-import-btn"
              >
                <Sparkles className="w-4 h-4" />
                {language === 'en' ? 'Smart Import' : 'স্মার্ট ইমপোর্ট'}
              </Button>
            {/* Add Part Button */}
            <Button
              className="gap-2 bg-blue-800 hover:bg-blue-900"
              onClick={() => { setEditingPart(null); setFormData({...emptyPart, category: lastCategoryRef.current}); setFieldErrors({}); userToggledConsumableRef.current = false; userChangedCategoryRef.current = false; userChangedMinStockRef.current = false; geminiCallCountRef.current = 0; setShowAddPart(true); }}
              data-testid="add-part-btn"
            >
              <Plus className="w-4 h-4" />
              {t('add_part')}
            </Button>
          </div>
        </div>
        )}

        {/* Smart Import View */}
        {viewMode === 'import' ? (
          <AIImportView
            onClose={() => { setViewMode('list'); fetchParts(true); axios.get(`${API}/import/batches/pending`).then(r => setPendingReviewCount(r.data?.pending_items || 0)).catch(() => {}); }}
            categories={allCategories}
            remindersEnabled={remindersEnabled}
            consumableCategories={consumableCategories}
          />
        ) : (
        <>

        {/* Pending Import Review Nudge */}
        {pendingReviewCount > 0 && viewMode === 'list' && (
          <div
            className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 cursor-pointer hover:bg-blue-100 transition-colors"
            onClick={() => setViewMode('import')}
          >
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-blue-600" />
              <p className="text-sm text-blue-800 font-medium">
                {language === 'en'
                  ? `${pendingReviewCount} scanned parts waiting for your review`
                  : `${pendingReviewCount}টি স্ক্যান করা পার্ট রিভিউ করতে হবে`}
              </p>
            </div>
            <Button size="sm" className="bg-blue-700 hover:bg-blue-800 text-white text-xs">
              {language === 'en' ? 'Review Now' : 'রিভিউ করুন'}
            </Button>
          </div>
        )}

        {/* Low Stock Alert */}
        {lowStockParts.length > 0 && (
          <Card className="border-red-200 bg-red-50">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-red-800">
                <AlertTriangle className="w-5 h-5" />
                {t('low_stock_items')} ({lowStockParts.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {lowStockParts.slice(0, 20).map((part) => (
                  <Badge 
                    key={part.id} 
                    variant="destructive" 
                    className="px-3 py-1 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => setSearch(part.name)}
                  >
                    {part.name}: {part.stock_quantity} {language === 'en' ? 'left' : 'বাকি'}
                  </Badge>
                ))}
                {lowStockParts.length > 20 && (
                  <Badge 
                    variant="outline" 
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => setShowLowStockOnly(true)}
                  >
                    +{lowStockParts.length - 20} {language === 'en' ? 'more' : 'আরো'}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder={language === 'en' ? 'Search by name, SKU, brand, category...' : 'নাম, SKU, ব্র্যান্ড, ক্যাটাগরি দিয়ে খুঁজুন...'}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 pr-9 h-12"
                  data-testid="inventory-search"
                />
                {search && (
                  <button
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-red-400 hover:text-red-600 transition-colors"
                    onClick={() => setSearch('')}
                    aria-label="Clear search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="flex-1 sm:flex-none sm:w-[180px] h-12" data-testid="category-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {filterCategories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {language === 'bn' ? cat.labelBn : cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant={showLowStockOnly ? 'default' : 'outline'}
                  className={`h-12 gap-2 whitespace-nowrap ${showLowStockOnly ? 'bg-red-600 hover:bg-red-700' : ''}`}
                  onClick={() => setShowLowStockOnly(!showLowStockOnly)}
                  data-testid="low-stock-filter"
                >
                  <AlertTriangle className="w-4 h-4" />
                  {language === 'en' ? 'Low Stock' : 'কম স্টক'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Parts Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Spinner size="lg" />
          </div>
        ) : parts.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {parts.map((part) => {
              const isLowStock = part.stock_quantity <= part.min_stock_level;
              const profit = part.selling_price - part.purchase_price;
              const profitMargin = part.purchase_price > 0 ? ((profit / part.purchase_price) * 100).toFixed(0) : 0;
              const priceRevealed = revealedPrices[part.id];
              
              return (
                <Card 
                  key={part.id} 
                  className={`${isLowStock ? 'border-red-300 bg-red-50' : ''}`}
                  data-testid={`part-card-${part.id}`}
                >
                  <CardContent className="p-3 sm:p-5">
                    <div className="flex items-start justify-between mb-2 sm:mb-3">
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold text-slate-900 text-sm sm:text-base truncate">
                          {language === 'bn' && part.name_bn ? part.name_bn : part.name}
                        </h3>
                        {part.sku && <p className="text-xs text-slate-500 truncate">{part.sku}</p>}
                      </div>
                      <Badge variant="secondary" className="capitalize text-[10px] sm:text-xs ml-1 shrink-0">{part.category}</Badge>
                    </div>

                    {part.brand && (
                      <p className="text-xs sm:text-sm text-slate-500 mb-2 sm:mb-3">{t('brand')}: {part.brand}</p>
                    )}

                    <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-3 sm:mb-4">
                      {/* Buying price - tap to toggle reveal */}
                      <div
                        className="cursor-pointer select-none rounded-md p-1.5 -m-1.5 hover:bg-slate-100 active:bg-slate-200 transition-colors"
                        onClick={() => toggleRevealPrice(part.id)}
                        title={language === 'en' ? 'Tap to reveal/hide' : 'দেখতে/লুকাতে ট্যাপ করুন'}
                      >
                        <p className="text-[10px] sm:text-xs text-slate-500 flex items-center gap-1">
                          {t('purchase_price')}
                          {priceRevealed ? (
                            <EyeOff className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-slate-400" />
                          ) : (
                            <Eye className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-slate-400" />
                          )}
                        </p>
                        <p className="font-semibold text-xs sm:text-base">
                          {priceRevealed ? formatCurrency(part.purchase_price, language) : '৳ ••••'}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] sm:text-xs text-slate-500">{t('selling_price')}</p>
                        <p className="font-semibold text-green-600 text-xs sm:text-base">{formatCurrency(part.selling_price, language)}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-2 sm:p-3 bg-slate-100 rounded-lg mb-2 sm:mb-3">
                      <div>
                        <p className="text-[10px] sm:text-xs text-slate-500">{t('stock_qty')}</p>
                        <p className={`text-lg sm:text-2xl font-bold ${isLowStock ? 'text-red-600' : 'text-slate-900'}`}>
                          {part.stock_quantity}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] sm:text-xs text-slate-500">{language === 'en' ? 'Margin' : 'মার্জিন'}</p>
                        <div className="flex items-center gap-1">
                          {profit > 0 ? (
                            <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />
                          ) : (
                            <TrendingDown className="w-3 h-3 sm:w-4 sm:h-4 text-red-600" />
                          )}
                          <span className={`font-bold text-xs sm:text-base ${profit > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {profitMargin}%
                          </span>
                        </div>
                      </div>
                    </div>

                    {isLowStock && (
                      <Badge variant="destructive" className="w-full justify-center mb-2 sm:mb-3 text-[10px] sm:text-xs">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        {language === 'en' ? 'Low Stock!' : 'স্টক কম!'}
                      </Badge>
                    )}

                    {/* Action buttons */}
                    <div className="flex gap-1 sm:gap-2">
                      <Button 
                        variant="outline" 
                        className="flex-1 text-xs sm:text-sm h-8 sm:h-9"
                        onClick={() => openAdjustStock(part)}
                        data-testid={`adjust-stock-${part.id}`}
                      >
                        {t('adjust_stock')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 sm:h-9 w-8 sm:w-9 p-0"
                        onClick={() => handleEditPart(part)}
                        data-testid={`edit-part-${part.id}`}
                      >
                        <Edit className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 sm:h-9 w-8 sm:w-9 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDeletePart(part.id)}
                        data-testid={`delete-part-${part.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="py-20 text-center">
              <Package className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500 text-lg">
                {language === 'en' ? 'No parts found' : 'কোন পার্ট পাওয়া যায়নি'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Load More button for pagination */}
        {!loading && parts.length > 0 && parts.length < totalCount && (
          <div className="flex justify-center">
            <Button
              variant="outline"
              className="gap-2"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? <Spinner size="sm" /> : null}
              {language === 'en'
                ? `Load More (${parts.length} of ${totalCount})`
                : `আরো লোড করুন (${parts.length} / ${totalCount})`}
            </Button>
          </div>
        )}

        {/* Add/Edit Part Dialog */}
        <Dialog open={showAddPart} onOpenChange={(open) => { setShowAddPart(open); if (!open) { setEditingPart(null); setFieldErrors({}); setConsumableCategoryId(''); setIsConsumable(true); setConsumableSuggest(null); userToggledConsumableRef.current = false; userChangedCategoryRef.current = false; userChangedMinStockRef.current = false; geminiCallCountRef.current = 0; } }}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPart ? (language === 'en' ? 'Edit Part' : 'পার্ট সম্পাদনা') : t('add_part')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <RequiredLabel>{language === 'en' ? 'Part Name (English)' : 'পার্টের নাম (ইংরেজি)'}</RequiredLabel>
                <Input
                  value={formData.name}
                  onChange={(e) => { setFormData(p => ({ ...p, name: e.target.value })); setFieldErrors(p => ({ ...p, name: false })); }}
                  onBlur={() => setFormData(p => ({ ...p, name: smartCapitalize(p.name) }))}
                  className={errorBorder('name')}
                  data-testid="new-part-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label>{language === 'en' ? 'Part Name (Bengali)' : 'পার্টের নাম (বাংলা)'}</Label>
                <Input
                  value={formData.name_bn}
                  onChange={(e) => setFormData(p => ({ ...p, name_bn: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label>{language === 'en' ? 'Part Number / SKU' : 'পার্ট নম্বর / SKU'}</Label>
                <Input
                  value={formData.sku}
                  onChange={(e) => setFormData(p => ({ ...p, sku: e.target.value }))}
                  placeholder="e.g. BRK-PAD-001"
                  data-testid="new-part-sku"
                />
              </div>
              {/* Category + Brand row - using subgrid pattern for perfect alignment */}
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                <RequiredLabel>{t('category')}</RequiredLabel>
                <Label>{t('brand')}</Label>
                <Select value={formData.category} onValueChange={(v) => { userChangedCategoryRef.current = true; setFormData(p => ({ ...p, category: v })); setFieldErrors(p => ({ ...p, category: false })); }}>
                  <SelectTrigger className={`h-10 ${errorBorder('category')}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {allCategories.map(c => (
                      <SelectItem key={c.value} value={c.value}>{language === 'bn' ? c.labelBn : c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={formData.brand}
                  onChange={(e) => setFormData(p => ({ ...p, brand: e.target.value }))}
                  className="h-10"
                />
              </div>
              {/* Purchase Price + Selling Price row */}
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                <RequiredLabel>{t('purchase_price')} (৳)</RequiredLabel>
                <RequiredLabel>{t('selling_price')} (৳)</RequiredLabel>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  value={formData.purchase_price}
                  onChange={(e) => { handleIntegerField('purchase_price', e.target.value); setFieldErrors(p => ({ ...p, purchase_price: false })); }}
                  onKeyDown={(e) => { if (['.', 'e', '-', '+'].includes(e.key)) e.preventDefault(); }}
                  className={errorBorder('purchase_price')}
                  data-testid="new-part-purchase"
                />
                <Input
                  type="number"
                  step="1"
                  min="0"
                  value={formData.selling_price}
                  onChange={(e) => { handleIntegerField('selling_price', e.target.value); setFieldErrors(p => ({ ...p, selling_price: false })); }}
                  onKeyDown={(e) => { if (['.', 'e', '-', '+'].includes(e.key)) e.preventDefault(); }}
                  className={errorBorder('selling_price')}
                  data-testid="new-part-selling"
                />
              </div>
              {/* Stock + Min Stock row */}
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                {!editingPart ? <RequiredLabel>{t('stock_qty')}</RequiredLabel> : <Label>{t('stock_qty')}</Label>}
                <Label>{t('min_stock')}</Label>
                <div>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    value={formData.stock_quantity}
                    onChange={(e) => { handleIntegerField('stock_quantity', e.target.value); setFieldErrors(p => ({ ...p, stock_quantity: false })); }}
                    onKeyDown={(e) => { if (e.key === '.' || e.key === 'e') e.preventDefault(); }}
                    className={`${errorBorder('stock_quantity')} ${editingPart ? 'bg-slate-100 text-slate-500' : ''}`}
                    disabled={!!editingPart}
                    data-testid="new-part-stock"
                  />
                  {editingPart && (
                    <p className="text-[10px] text-slate-400 mt-1">{language === 'en' ? 'Use "Adjust Stock" to change' : '"স্টক সমন্বয়" ব্যবহার করুন'}</p>
                  )}
                </div>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  value={formData.min_stock_level}
                  onChange={(e) => { userChangedMinStockRef.current = true; handleIntegerField('min_stock_level', e.target.value); }}
                  onKeyDown={(e) => { if (e.key === '.' || e.key === 'e') e.preventDefault(); }}
                />
              </div>
              {/* Consumable Category */}
              {remindersEnabled && consumableCategories.length > 0 && (
                <div className="space-y-2 border-t pt-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">{language === 'en' ? 'Consumable Category' : 'কনজিউমেবল ক্যাটাগরি'}</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">{language === 'en' ? 'Not consumable' : 'কনজিউমেবল নয়'}</span>
                      <Switch checked={!isConsumable} onCheckedChange={(v) => {
                        userToggledConsumableRef.current = true;
                        setIsConsumable(!v);
                        if (v) {
                          // When marking as not consumable, auto-select the "Not Consumable" category
                          const notConsumCat = consumableCategories.find(c => c.is_non_consumable || c.name?.toLowerCase() === 'not consumable');
                          setConsumableCategoryId(notConsumCat ? notConsumCat.id : '');
                          setConsumableSuggest(null);
                        } else {
                          setConsumableCategoryId('');
                          setConsumableSuggest(null);
                        }
                      }} />
                    </div>
                  </div>
                  {isConsumable && (
                    <Select value={consumableCategoryId} onValueChange={setConsumableCategoryId}>
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder={language === 'en' ? 'Select category...' : 'ক্যাটাগরি নির্বাচন করুন...'} />
                      </SelectTrigger>
                      <SelectContent>
                        {consumableCategories.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {isConsumable && consumableSuggest && consumableCategoryId === consumableSuggest.suggested_category_id && (
                    <p className="text-[10px] text-slate-400">{language === 'en' ? 'Auto-suggested' : 'স্বয়ংক্রিয়ভাবে নির্বাচিত'}</p>
                  )}
                </div>
              )}
              <Button onClick={handleSavePart} className="w-full" disabled={saving} data-testid="submit-part">
                {saving ? <Spinner size="sm" className="mr-2" /> : null}
                {editingPart ? (language === 'en' ? 'Update Part' : 'পার্ট আপডেট করুন') : (language === 'en' ? 'Add Part' : 'পার্ট যোগ করুন')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Adjust Stock Dialog */}
        <Dialog open={showAdjustDialog} onOpenChange={(open) => { setShowAdjustDialog(open); if (!open) setAdjustingPart(null); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>{t('adjust_stock')}: {adjustingPart?.name}</DialogTitle>
            </DialogHeader>
            {adjustingPart && (
              <div className="space-y-4">
                <div className="p-3 bg-slate-100 rounded-lg text-center">
                  <p className="text-xs text-slate-500">{language === 'en' ? 'Current Stock' : 'বর্তমান স্টক'}</p>
                  <p className="text-3xl font-bold text-slate-900">{adjustingPart.stock_quantity}</p>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <Button
                    variant={adjustMode === 'set' ? 'default' : 'outline'}
                    size="sm"
                    className="text-xs"
                    onClick={() => { setAdjustMode('set'); setAdjustmentValue(adjustingPart.stock_quantity.toString()); }}
                  >
                    {language === 'en' ? 'Set To' : 'সেট করুন'}
                  </Button>
                  <Button
                    variant={adjustMode === 'add' ? 'default' : 'outline'}
                    size="sm"
                    className="text-xs"
                    onClick={() => { setAdjustMode('add'); setAdjustmentValue(''); }}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    {language === 'en' ? 'Add' : 'যোগ'}
                  </Button>
                  <Button
                    variant={adjustMode === 'remove' ? 'default' : 'outline'}
                    size="sm"
                    className="text-xs"
                    onClick={() => { setAdjustMode('remove'); setAdjustmentValue(''); }}
                  >
                    <Minus className="w-3 h-3 mr-1" />
                    {language === 'en' ? 'Remove' : 'সরান'}
                  </Button>
                </div>

                <div>
                  <Label>
                    {adjustMode === 'set' ? (language === 'en' ? 'New Stock Amount' : 'নতুন স্টক পরিমাণ') :
                     adjustMode === 'add' ? (language === 'en' ? 'Quantity to Add' : 'যোগ করার পরিমাণ') :
                     (language === 'en' ? 'Quantity to Remove' : 'সরানোর পরিমাণ')}
                  </Label>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    ref={adjustInputRef}
                    value={adjustmentValue}
                    onChange={(e) => setAdjustmentValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === '.' || e.key === 'e') e.preventDefault(); }}
                    className="text-center text-lg font-bold"
                    data-testid="adjustment-input"
                  />
                </div>

                <div className="p-3 bg-blue-50 rounded-lg text-center border border-blue-200">
                  <p className="text-xs text-blue-600">{language === 'en' ? 'New Stock Will Be' : 'নতুন স্টক হবে'}</p>
                  <p className="text-2xl font-bold text-blue-800">{getNewStockQuantity()}</p>
                </div>

                <Button onClick={handleAdjustStock} className="w-full" data-testid="confirm-adjustment">
                  {language === 'en' ? 'Confirm' : 'নিশ্চিত করুন'}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Category Settings Dialog */}
        <Dialog open={showCategorySettings} onOpenChange={setShowCategorySettings}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{language === 'en' ? 'Manage Categories' : 'ক্যাটাগরি পরিচালনা'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Default categories with toggle */}
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">{language === 'en' ? 'Default Categories' : 'ডিফল্ট ক্যাটাগরি'}</p>
                <div className="space-y-2">
                  {DEFAULT_CATEGORIES.map(cat => (
                    <div key={cat.value} className="flex items-center justify-between p-2 rounded-lg border bg-slate-50">
                      <span className="text-sm">{language === 'bn' ? cat.labelBn : cat.label}</span>
                      <Button
                        variant={disabledDefaults.includes(cat.value) ? 'outline' : 'default'}
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => toggleDefaultCategory(cat.value)}
                      >
                        {disabledDefaults.includes(cat.value)
                          ? (language === 'en' ? 'Disabled' : 'নিষ্ক্রিয়')
                          : (language === 'en' ? 'Active' : 'সক্রিয়')
                        }
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Custom categories */}
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">{language === 'en' ? 'Custom Categories' : 'কাস্টম ক্যাটাগরি'}</p>
                <div className="space-y-2">
                  {customCategories.map(cat => (
                    <div key={cat.id} className="flex items-center gap-2 p-2 rounded-lg border">
                      {editingCategory?.id === cat.id ? (
                        <div className="flex flex-col gap-1.5 w-full">
                          <div className="flex gap-2">
                            <Input
                              value={editingCategory.name}
                              onChange={(e) => setEditingCategory(p => ({ ...p, name: e.target.value }))}
                              className="h-7 text-sm flex-1"
                              placeholder={language === 'en' ? 'English name' : 'ইংরেজি নাম'}
                            />
                            <Input
                              value={editingCategory.name_bn || ''}
                              onChange={(e) => setEditingCategory(p => ({ ...p, name_bn: e.target.value }))}
                              className="h-7 text-sm flex-1"
                              placeholder={language === 'en' ? 'বাংলা নাম' : 'বাংলা নাম'}
                            />
                          </div>
                          <div className="flex gap-2 justify-end">
                            <Button size="sm" className="h-7 text-xs" onClick={() => handleUpdateCategory(cat.id)}>
                              {language === 'en' ? 'Save' : 'সংরক্ষণ'}
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditingCategory(null)}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <span className={`text-sm flex-1 ${cat.is_active === false ? 'text-slate-400 line-through' : ''}`}>
                            {cat.name}{cat.name_bn ? ` (${cat.name_bn})` : ''}
                          </span>
                          <Button
                            size="sm"
                            variant={cat.is_active === false ? 'outline' : 'default'}
                            className="h-7 text-xs"
                            onClick={() => handleToggleCustomCategory(cat.id)}
                          >
                            {cat.is_active === false
                              ? (language === 'en' ? 'Disabled' : 'নিষ্ক্রিয়')
                              : (language === 'en' ? 'Active' : 'সক্রিয়')
                            }
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditingCategory(cat)}>
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600" onClick={() => handleDeleteCategory(cat.id)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  ))}
                  {customCategories.length === 0 && (
                    <p className="text-xs text-slate-400 text-center py-2">{language === 'en' ? 'No custom categories yet' : 'এখনো কোনো কাস্টম ক্যাটাগরি নেই'}</p>
                  )}
                </div>
              </div>

              {/* Add new category */}
              <div className="border-t pt-4">
                <p className="text-sm font-medium text-slate-700 mb-2">{language === 'en' ? 'Add New Category' : 'নতুন ক্যাটাগরি যোগ করুন'}</p>
                <div className="flex gap-2">
                  <Input
                    placeholder={language === 'en' ? 'Name (English)' : 'নাম (ইংরেজি)'}
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    className="h-9 text-sm"
                  />
                  <Input
                    placeholder={language === 'en' ? 'Name (বাংলা)' : 'নাম (বাংলা)'}
                    value={newCategoryNameBn}
                    onChange={(e) => setNewCategoryNameBn(e.target.value)}
                    className="h-9 text-sm"
                  />
                  <Button size="sm" className="h-9" onClick={handleAddCategory}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Custom Confirm Dialog (replaces browser confirm) */}
        <AlertDialog open={confirmDialog.open} onOpenChange={(open) => { if (!open) setConfirmDialog(p => ({ ...p, open: false })); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{confirmDialog.title}</AlertDialogTitle>
              <AlertDialogDescription>{confirmDialog.description}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setConfirmDialog(p => ({ ...p, open: false }))}>
                {language === 'en' ? 'Cancel' : 'বাতিল'}
              </AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-600 hover:bg-red-700"
                onClick={() => { confirmDialog.onConfirm?.(); setConfirmDialog(p => ({ ...p, open: false })); }}
              >
                {language === 'en' ? 'Delete' : 'মুছুন'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        </>
        )}
      </div>

      {/* Premium Upgrade Dialog for Smart Import */}
      <PremiumUpgradeDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        feature={language === 'en' ? 'AI Smart Import' : 'AI স্মার্ট ইমপোর্ট'}
      />
    </AdminLayout>
  );
}
